import { makeAutoObservable, runInAction } from 'mobx';

import { SurveyForm } from '~modules/surveys/forms/SurveyForm';
import { SurveyResultCommentForm } from '~modules/surveys/forms/SurveyResultCommentForm';
import { SurveyExtraQuestionModel } from '~modules/surveys/models/SurveyExtraQuestionModel';
import { SurveyModel } from '~modules/surveys/models/SurveyModel';
import { SurveyQuestionModel } from '~modules/surveys/models/SurveyQuestionModel';
import { SurveyResultsModel } from '~modules/surveys/models/SurveyResultsModel';
import { IBaseSurveyProps } from '~modules/surveys/types/IBaseSurveyProps';

import SurveyService from './SurveyService';

export class SurveyStore {
  survey: SurveyModel | null = null;
  surveyLoading = true;
  surveyLoaded = true;

  surveyResults: SurveyResultsModel | null = null;
  surveyResultsLoading = true;
  surveyResultsLoaded = true;

  sendSurveyLoading = false;
  postCommentsLoading = false;
  updateCommentsLoading = false;

  private service: SurveyService;

  constructor() {
    makeAutoObservable(this);
    this.service = new SurveyService();
  }

  // API

  getSurvey = (props: IBaseSurveyProps) => {
    this.surveyLoading = true;

    this.service
      .getSurvey(props)
      .then((data) =>
        runInAction(() => {
          this.survey = data;
          this.surveyLoaded = true;
        }),
      )
      .catch(() =>
        runInAction(() => {
          this.surveyLoaded = false;
        }),
      )
      .finally(() =>
        runInAction(() => {
          this.surveyLoading = false;
        }),
      );
  };

  sendSurvey = (form: SurveyForm, survey: SurveyModel, baseProps: IBaseSurveyProps, onSuccess?: () => void) => {
    this.sendSurveyLoading = true;

    return this.service
      .sendSurvey(form, survey, baseProps)
      .then(() => {
        onSuccess?.();
      })
      .catch(() => {})
      .finally(() =>
        runInAction(() => {
          this.sendSurveyLoading = false;
        }),
      );
  };

  getSurveyResults = (props: IBaseSurveyProps) => {
    this.surveyResultsLoading = true;

    this.service
      .getSurveyResults(props)
      .then((data) =>
        runInAction(() => {
          this.surveyResults = data;
          this.surveyResultsLoaded = true;
        }),
      )
      .catch(() =>
        runInAction(() => {
          this.surveyResultsLoaded = false;
        }),
      )
      .finally(() =>
        runInAction(() => {
          this.surveyResultsLoading = false;
        }),
      );
  };

  closeSurvey = (form: SurveyResultCommentForm, baseProps: IBaseSurveyProps, onSuccess?: () => void) => {
    this.postCommentsLoading = true;

    return this.service
      .closeSurvey(form, baseProps)
      .then(() => {
        onSuccess?.();
      })
      .catch(() => {})
      .finally(() =>
        runInAction(() => {
          this.postCommentsLoading = false;
        }),
      );
  };

  saveCommentsFromDealer = (form: SurveyResultCommentForm, baseProps: IBaseSurveyProps, onSuccess?: () => void) => {
    this.updateCommentsLoading = true;

    return this.service
      .saveCommentsFromDealer(form, baseProps)
      .then(() => {
        onSuccess?.();
      })
      .catch(() => {})
      .finally(() =>
        runInAction(() => {
          this.updateCommentsLoading = false;
        }),
      );
  };

  // RESET

  reset = () => {
    this.survey = null;
    this.surveyLoading = true;
    this.surveyLoaded = true;

    this.surveyResults = null;
    this.surveyResultsLoading = true;
    this.surveyResultsLoaded = true;
  };

  //OTHERS

  prepareSurveyFormDefaultValue = (data: SurveyQuestionModel[]) => {
    return this.service.prepareSurveyFormDefaultValue(data);
  };

  prepareSurveyFormField = (data: SurveyQuestionModel) => {
    return this.service.prepareSurveyFormField(data);
  };

  prepareDefaultExtraQuestion = (data?: SurveyExtraQuestionModel) => {
    return this.service.prepareDefaultExtraQuestion(data);
  };

  prepareSurveyResultCommentFormValues = (data: SurveyResultsModel) => {
    return this.service.prepareSurveyResultCommentFormValues(data);
  };
}
